import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Package,
  Calendar,
  CreditCard,
  MapPin,
  Truck,
  ExternalLink,
  Star,
  XCircle,
  ChevronRight,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import PageLoadingState from "../components/PageLoadingState";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ImageModal({ isOpen, onClose, imageUrl, productName }) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[90vw] md:max-w-[70vw] lg:max-w-[50vw] p-0 overflow-hidden bg-black/90 border-white/10">
        <DialogTitle className="sr-only">
          Enlarged view of {productName}
        </DialogTitle>
        <div className="relative w-full h-full flex items-center justify-center min-h-[50vh]">
          <img
            src={imageUrl}
            alt={productName}
            className="max-w-full max-h-[85vh] object-contain"
          />
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
            <p className="text-white text-center font-medium">{productName}</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReviewModal({
  isOpen,
  onClose,
  productId,
  productName,
  onReviewSubmitted,
}) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!comment.trim()) {
      toast.error("Please enter a comment");
      return;
    }

    setSubmitting(true);
    const token = sessionStorage.getItem("token");
    try {
      await axios.post(
        `${API}/products/${productId}/reviews`,
        {
          rating,
          comment,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success("Review submitted successfully");
      onReviewSubmitted();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px] glass-morphic border-white/10">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold">
            Rate & Review
          </DialogTitle>
          <p className="text-muted-foreground">
            Share your experience with {productName}
          </p>
        </DialogHeader>
        <div className="py-6 space-y-6">
          <div className="flex flex-col items-center gap-3">
            <p className="text-sm font-medium">Your Rating</p>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setRating(star)}
                  className="transition-transform hover:scale-110"
                >
                  <Star
                    className={`h-8 w-8 ${
                      star <= rating
                        ? "fill-yellow-500 text-yellow-500"
                        : "text-muted-foreground"
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <p className="text-sm font-medium">Your Review</p>
            <Textarea
              placeholder="Tell others what you think about this product..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="bg-white/5 border-white/10 min-h-[100px] resize-none focus:ring-primary"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="rounded-full bg-primary hover:bg-primary/90"
          >
            {submitting ? "Submitting..." : "Submit Review"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewModal, setReviewModal] = useState({
    isOpen: false,
    productId: null,
    productName: "",
  });
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [imageModal, setImageModal] = useState({
    isOpen: false,
    imageUrl: "",
    productName: "",
  });
  const [cancelling, setCancelling] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    const token = sessionStorage.getItem("token");
    try {
      const response = await axios.get(`${API}/orders`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrders(response.data);
    } catch (error) {
      toast.error("Failed to fetch orders");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelOrder = async (orderId) => {
    if (!window.confirm("Are you sure you want to cancel this order?")) return;

    setCancelling(true);
    const token = sessionStorage.getItem("token");
    try {
      await axios.post(
        `${API}/orders/${orderId}/cancel`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success("Order cancelled successfully");
      fetchOrders();
      setIsDetailsOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel order");
    } finally {
      setCancelling(false);
    }
  };

  const getCourierTrackingUrl = (courier, trackingNumber) => {
    const urls = {
      bluedart: `https://www.bluedart.com/web/guest/trackdartresult?trackFor=0&trackNo=${trackingNumber}`,
      delhivery: `https://www.delhivery.com/track/package/${trackingNumber}`,
      dtdc: `https://www.dtdc.in/tracking.asp`,
      ecom: `https://ecomexpress.in/tracking/?awb_field=${trackingNumber}`,
      fedex: `https://www.fedex.com/fedextrack/?tracknumbers=${trackingNumber}`,
      indiapost: `https://www.indiapost.gov.in/_layouts/15/dop.portal.tracking/trackconsignment.aspx`,
      professional: `https://www.tpcindia.com/`,
      shadowfax: `https://shadowfax.in/track-order/?order_id=${trackingNumber}`,
      xpressbees: `https://www.xpressbees.com/shipment/tracking?tracking_id=${trackingNumber}`,
    };
    return urls[courier] || "#";
  };

  const getStatusColor = (status) => {
    const colors = {
      placed: "bg-blue-500/10 text-blue-500 border-blue-500/30",
      accepted: "bg-purple-500/10 text-purple-500 border-purple-500/30",
      confirmed: "bg-green-500/10 text-green-500 border-green-500/30",
      packed: "bg-yellow-500/10 text-yellow-500 border-yellow-500/30",
      shipped: "bg-indigo-500/10 text-indigo-500 border-indigo-500/30",
      delivered: "bg-accent/10 text-accent border-accent/30",
      cancelled: "bg-destructive/10 text-destructive border-destructive/30",
    };
    return colors[status] || "bg-primary/10 text-primary border-primary/30";
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      paid: "bg-green-500/10 text-green-500 border-green-500/30",
      pending: "bg-yellow-500/10 text-yellow-500 border-yellow-500/30",
      "payment failed": "bg-red-500/10 text-red-500 border-red-500/30",
      "Cash on Delivery": "bg-blue-500/10 text-blue-500 border-blue-500/30",
    };
    return colors[status] || "bg-primary/10 text-primary border-primary/30";
  };

  const getStatusSteps = () => {
    return [
      "placed",
      "accepted",
      "confirmed",
      "packed",
      "shipped",
      "delivered",
    ];
  };

  const getStatusProgress = (currentStatus) => {
    const steps = getStatusSteps();
    const currentIndex = steps.indexOf(currentStatus);
    return ((currentIndex + 1) / steps.length) * 100;
  };

  if (loading) {
    return (
      <PageLoadingState title="Loading your orders" />
    );
  }

  return (
    <div
      className="min-h-screen pt-32 px-6 md:px-12 pb-20"
      data-testid="orders-page"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1
            className="text-4xl md:text-5xl font-semibold tracking-tight mb-12"
            data-testid="orders-title"
          >
            My Orders
          </h1>

          <div className="flex flex-wrap gap-2 mb-8 overflow-x-auto pb-2 scrollbar-hide">
            {["all", ...getStatusSteps(), "cancelled"].map((status) => (
              <Button
                key={status}
                variant={filterStatus === status ? "default" : "outline"}
                size="sm"
                onClick={() => setFilterStatus(status)}
                className={`rounded-full capitalize whitespace-nowrap ${
                  filterStatus === status
                    ? "bg-primary text-primary-foreground shadow-lg shadow-primary/20"
                    : "bg-white/5 border-white/10 hover:bg-white/10"
                }`}
              >
                {status}
              </Button>
            ))}
          </div>

          {orders.filter(o => filterStatus === "all" || o.order_status === filterStatus).length > 0 ? (
            <div className="grid gap-4">
              {orders
                .filter(o => filterStatus === "all" || o.order_status === filterStatus)
                .map((order, index) => (
                <motion.div
                  key={order.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  onClick={() => {
                    setSelectedOrder(order);
                    setIsDetailsOpen(true);
                  }}
                  className="glass-morphic rounded-xl p-4 md:p-6 cursor-pointer hover:border-primary/50 transition-all group"
                  data-testid={`order-${order.id}`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div 
                        className="h-16 w-16 rounded-xl overflow-hidden bg-white/5 flex-shrink-0 border border-white/10 group-hover:border-primary/30 transition-colors cursor-zoom-in"
                        onClick={(e) => {
                          e.stopPropagation();
                          setImageModal({
                            isOpen: true,
                            imageUrl: order.items[0].product_image,
                            productName: order.items[0].product_name || "Product",
                          });
                        }}
                      >
                        {order.items[0]?.product_image ? (
                          <img
                            src={order.items[0].product_image}
                            alt="Product"
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Package className="h-8 w-8 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                      <div>
                        <p className="font-mono text-sm font-semibold text-primary">
                          #{order.id.slice(0, 8).toUpperCase()}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(order.created_at).toLocaleDateString(
                            "en-IN",
                            {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            }
                          )}
                        </p>
                        <div className="flex items-center gap-2 mt-2 sm:hidden">
                           <p className="text-sm font-bold">
                            ₹{order.total_amount.toFixed(2)}
                          </p>
                          <span className="text-[10px] text-muted-foreground">• {order.items.length} items</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 ml-auto md:ml-0">
                      <div className="text-right hidden sm:block">
                        <p className="text-sm font-bold text-foreground">
                          ₹{order.total_amount.toFixed(2)}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {order.items.length} items
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-2 ml-auto md:ml-0">
                        <span
                          className={`px-3 py-1.5 rounded-full text-[10px] sm:text-xs font-bold border ${getStatusColor(
                            order.order_status
                          )}`}
                        >
                          {order.order_status.toUpperCase()}
                        </span>
                        <span
                          className={`px-3 py-1 rounded-full text-[9px] sm:text-[10px] font-medium border ${getPaymentStatusColor(
                            order.payment_status
                          )}`}
                        >
                          {order.payment_status?.toUpperCase()}
                        </span>
                      </div>
                      <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20 bg-white/5 rounded-3xl border border-dashed border-white/10" data-testid="no-orders">
              <Package className="h-16 w-16 mx-auto mb-4 text-muted-foreground/30" />
              <p className="text-xl text-muted-foreground">No {filterStatus !== "all" ? filterStatus : ""} orders found</p>
              {filterStatus !== "all" && (
                <Button 
                  variant="link" 
                  onClick={() => setFilterStatus("all")}
                  className="mt-2 text-primary"
                >
                  View all orders
                </Button>
              )}
            </div>
          )}
        </motion.div>
      </div>

      <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto glass-morphic border-white/10">
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold flex items-center gap-3">
              Order Details
              {selectedOrder && (
                <span className="font-mono text-sm font-normal text-muted-foreground">
                  #{selectedOrder.id.slice(0, 8).toUpperCase()}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          {selectedOrder && (
            <div className="space-y-8 py-4">
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-primary" />
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">
                        Order Date
                      </p>
                      <p className="text-sm font-medium">
                        {new Date(selectedOrder.created_at).toLocaleDateString(
                          "en-IN",
                          {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          }
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <CreditCard className="h-5 w-5 text-primary" />
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">
                        Payment Info
                      </p>
                      <div className="flex flex-col gap-1">
                        <p className="text-sm font-medium capitalize">
                          {selectedOrder.payment_method === "cod"
                            ? "Cash on Delivery"
                            : "Online Payment"}
                        </p>
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-md text-[10px] font-bold border w-fit ${getPaymentStatusColor(
                            selectedOrder.payment_status
                          )}`}
                        >
                          {selectedOrder.payment_status?.toUpperCase()}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-primary mt-1" />
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">
                        Shipping Address
                      </p>
                      <div className="text-sm font-medium space-y-1">
                        <p>{selectedOrder.shipping_address.fullName}</p>
                        <p>{selectedOrder.shipping_address.address}</p>
                        <p>
                          {selectedOrder.shipping_address.city},{" "}
                          {selectedOrder.shipping_address.state} -{" "}
                          {selectedOrder.shipping_address.pincode}
                        </p>
                        <p>Phone: {selectedOrder.shipping_address.phone}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">
                      Order Status
                    </p>
                    <span
                      className={`inline-flex px-4 py-2 rounded-full font-semibold text-sm border ${getStatusColor(
                        selectedOrder.order_status
                      )}`}
                    >
                      {selectedOrder.order_status.toUpperCase()}
                    </span>
                  </div>

                  {selectedOrder.order_status !== "cancelled" && (
                    <div className="space-y-3">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">
                        Tracking Progress
                      </p>
                      <div className="w-full bg-white/5 rounded-full h-2">
                        <div
                          className="bg-primary h-2 rounded-full transition-all duration-500"
                          style={{
                            width: `${getStatusProgress(
                              selectedOrder.order_status
                            )}%`,
                          }}
                        />
                      </div>
                      <div className="flex justify-between text-[10px] text-muted-foreground">
                        {getStatusSteps().map((step) => (
                          <span
                            key={step}
                            className={
                              selectedOrder.order_status === step
                                ? "text-primary font-bold"
                                : ""
                            }
                          >
                            {step.charAt(0).toUpperCase() + step.slice(1)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedOrder.delivery_timeframe && (
                    <div className="p-4 rounded-xl bg-white/5 border border-white/5">
                      <div className="flex items-center gap-2 mb-1">
                        <Calendar className="h-4 w-4 text-primary" />
                        <p className="text-xs font-semibold uppercase tracking-wider">
                          Delivery Estimate
                        </p>
                      </div>
                      <p className="text-sm font-medium text-primary">
                        {selectedOrder.delivery_timeframe}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {(selectedOrder.order_status === "shipped" ||
                selectedOrder.order_status === "delivered") &&
                selectedOrder.tracking_number && (
                  <div className="p-4 rounded-2xl bg-primary/5 border border-primary/10">
                    <div className="flex items-center gap-3 mb-4">
                      <Truck className="h-6 w-6 text-primary" />
                      <h3 className="font-semibold">Tracking Information</h3>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">
                          Courier
                        </p>
                        <p className="text-sm font-bold capitalize">
                          {selectedOrder.courier_service?.replace("_", " ")}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">
                          Tracking ID
                        </p>
                        <a
                          href={getCourierTrackingUrl(
                            selectedOrder.courier_service,
                            selectedOrder.tracking_number
                          )}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-sm font-mono font-bold text-primary hover:underline"
                        >
                          {selectedOrder.tracking_number}
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </div>
                    </div>
                    <Button
                      onClick={() =>
                        window.open(
                          getCourierTrackingUrl(
                            selectedOrder.courier_service,
                            selectedOrder.tracking_number
                          ),
                          "_blank"
                        )
                      }
                      className="w-full mt-4 rounded-full"
                    >
                      Track Order
                    </Button>
                  </div>
                )}

              <div className="space-y-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <Package className="h-5 w-5 text-primary" />
                  Order Items
                </h3>
                <div className="grid gap-3">
                  {selectedOrder.items.map((item, idx) => (
                    <div
                      key={idx}
                      className="flex gap-4 items-center bg-white/5 p-4 rounded-2xl border border-white/5"
                    >
                      {item.product_image && (
                        <img
                          src={item.product_image}
                          alt={item.product_name}
                          className="w-16 h-16 object-cover rounded-xl shadow-lg cursor-zoom-in hover:scale-105 transition-transform"
                          onClick={() => setImageModal({
                            isOpen: true,
                            imageUrl: item.product_image,
                            productName: item.product_name || "Product",
                          })}
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <Link
                          to={`/products/${item.product_id}`}
                          className="text-sm font-bold hover:text-primary transition-colors block truncate"
                        >
                          {item.product_name ||
                            `Product ${item.product_id.slice(0, 8)}`}
                        </Link>
                        <p className="text-xs text-muted-foreground">
                          {item.color && `Colour: ${item.color} • `}
                          Quantity: {item.quantity} • ₹
                          {item.product_price?.toFixed(2) || "0.00"}
                        </p>
                        {item.note && (
                          <div className="mt-1 bg-white/5 p-1.5 px-2.5 rounded-lg inline-block border border-white/5">
                            <p className="text-[10px] text-primary uppercase tracking-wider font-semibold">Note</p>
                            <p className="text-xs italic text-muted-foreground">"{item.note}"</p>
                          </div>
                        )}
                      </div>
                      {selectedOrder.order_status === "delivered" && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setReviewModal({
                              isOpen: true,
                              productId: item.product_id,
                              productName: item.product_name || "this product",
                            });
                          }}
                          className="rounded-full text-xs"
                        >
                          <Star className="h-3 w-3 mr-1" />
                          Rate
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="pt-6 border-t border-white/10 flex flex-wrap items-center justify-between gap-6">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                    Total Paid
                  </p>
                  <p className="text-3xl font-bold text-primary">
                    ₹{selectedOrder.total_amount.toFixed(2)}
                  </p>
                </div>

                <div className="flex gap-3">
                  {(selectedOrder.order_status === "placed" ||
                    selectedOrder.order_status === "accepted") && (
                    <Button
                      variant="destructive"
                      onClick={() => handleCancelOrder(selectedOrder.id)}
                      disabled={cancelling}
                      className="rounded-full px-6"
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      {cancelling ? "Cancelling..." : "Cancel Order"}
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => setIsDetailsOpen(false)}
                    className="rounded-full px-6"
                  >
                    Close
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ReviewModal
        isOpen={reviewModal.isOpen}
        onClose={() => setReviewModal({ ...reviewModal, isOpen: false })}
        productId={reviewModal.productId}
        productName={reviewModal.productName}
        onReviewSubmitted={fetchOrders}
      />

      <ImageModal
        isOpen={imageModal.isOpen}
        onClose={() => setImageModal({ ...imageModal, isOpen: false })}
        imageUrl={imageModal.imageUrl}
        productName={imageModal.productName}
      />
    </div>
  );
}
